import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Trash2,
  RotateCcw,
  Package,
  Truck,
  User,
  Clock,
  AlertTriangle,
} from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";

const TrashPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTrash();
  }, []);

  const fetchTrash = async () => {
    try {
      const response = await api.get("/trash");
      setItems(response.data);
    } catch (error) {
      toast.error("Erro ao carregar lixeira");
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (item) => {
    try {
      await api.post(`/trash/${item.id}/restore`);
      toast.success("Item restaurado com sucesso!");
      fetchTrash();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao restaurar item");
    }
  };

  const handlePermanentDelete = async (item) => {
    try {
      await api.delete(`/trash/${item.id}`);
      toast.success("Item excluído permanentemente!");
      fetchTrash();
    } catch (error) {
      toast.error("Erro ao excluir item");
    }
  };

  const formatDateTime = (dateStr) => {
    try {
      return format(new Date(dateStr), "dd/MM/yyyy HH:mm", { locale: ptBR });
    } catch {
      return dateStr;
    }
  };

  const getTimeRemaining = (expiresAt) => {
    try {
      const expDate = new Date(expiresAt);
      if (expDate < new Date()) return "Expirado";
      return formatDistanceToNow(expDate, { locale: ptBR, addSuffix: true });
    } catch {
      return "-";
    }
  };

  const getEntityIcon = (entityType) => {
    switch (entityType) {
      case "product":
        return <Package className="h-5 w-5 text-blue-500" />;
      case "supplier":
        return <Truck className="h-5 w-5 text-emerald-500" />;
      case "user":
        return <User className="h-5 w-5 text-purple-500" />;
      default:
        return <Trash2 className="h-5 w-5 text-slate-500" />;
    }
  };

  const getEntityLabel = (entityType) => {
    const labels = {
      product: "Produto",
      supplier: "Fornecedor",
      user: "Usuário",
    };
    return labels[entityType] || entityType;
  };

  const getEntityName = (item) => {
    return item.entity_data?.name || item.entity_data?.email || "Sem nome";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="trash-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900">
          Lixeira
        </h1>
        <p className="text-slate-500 mt-1">
          Itens excluídos são mantidos por 24 horas antes da exclusão permanente
        </p>
      </div>

      {/* Warning */}
      <Card className="bg-amber-50 border-amber-200">
        <CardContent className="flex items-center gap-4 py-4">
          <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
          </div>
          <div>
            <p className="font-medium text-amber-900">
              Atenção: Itens expiram automaticamente
            </p>
            <p className="text-sm text-amber-700">
              Os itens na lixeira são excluídos permanentemente após 24 horas
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Trash Table */}
      <Card className="card-dashboard">
        <CardHeader>
          <CardTitle className="font-heading text-lg">
            Itens na Lixeira
          </CardTitle>
          <CardDescription>
            {items.length} item(ns) aguardando restauração ou exclusão permanente
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-500">
              <Trash2 className="h-12 w-12 mb-4 text-slate-300" />
              <p className="font-medium">Lixeira vazia</p>
              <p className="text-sm">Nenhum item excluído recentemente</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Item</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Excluído por</TableHead>
                    <TableHead>Excluído em</TableHead>
                    <TableHead>Expira</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((item) => (
                    <TableRow key={item.id} className="table-row-zebra" data-testid={`trash-row-${item.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                            {getEntityIcon(item.entity_type)}
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">
                              {getEntityName(item)}
                            </p>
                            {item.entity_data?.email && (
                              <p className="text-sm text-slate-500">
                                {item.entity_data.email}
                              </p>
                            )}
                            {item.entity_data?.category && (
                              <p className="text-sm text-slate-500">
                                {item.entity_data.category}
                              </p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">
                          {getEntityLabel(item.entity_type)}
                        </Badge>
                      </TableCell>
                      <TableCell>{item.deleted_by}</TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {formatDateTime(item.deleted_at)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-slate-400" />
                          <span className="text-sm text-slate-600">
                            {getTimeRemaining(item.expires_at)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRestore(item)}
                            className="gap-2"
                            data-testid={`restore-${item.id}`}
                          >
                            <RotateCcw className="h-4 w-4" />
                            Restaurar
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="destructive"
                                size="sm"
                                className="gap-2"
                                data-testid={`delete-permanently-${item.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                                Excluir
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>
                                  Excluir permanentemente?
                                </AlertDialogTitle>
                                <AlertDialogDescription>
                                  Esta ação não pode ser desfeita. O item será
                                  removido permanentemente do sistema.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handlePermanentDelete(item)}
                                  className="bg-red-600 hover:bg-red-700"
                                >
                                  Excluir Permanentemente
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TrashPage;
